// Replicate safeCacheStorage here as it's not exported from App
const safeCacheStorage = {
  getItem: (key: string) => {
    try {
      const localValue = localStorage.getItem(key);
      if (localValue) {
        return localValue;
      }
      try {
        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue) {
          try { localStorage.setItem(key, sessionValue); } catch (e) { /* ignore */ }
          return sessionValue;
        }
      } catch (sessionError: any) {
        console.error(`[Storage Error] SessionStorage fallback failed:`, sessionError);
      }
      return null;
    } catch (e: any) {
      console.error(`[Storage Error] Failed to get ${key}:`, e);
      try { return sessionStorage.getItem(key); } catch { return null; }
    }
  },
  setItem: (key: string, value: string) => {
    let localSuccess = false, sessionSuccess = false;
    try {
      localStorage.setItem(key, value);
      localSuccess = true;
    } catch (e: any) {
      console.error(`[Storage Error] localStorage failed for ${key}:`, e);
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        try {
          localStorage.removeItem('searchResultsCache');
          localStorage.removeItem('starterGridCache');
          try { localStorage.setItem(key, value); localSuccess = true; } catch (retryError: any) { /* ignore */ }
        } catch (clearError: any) { /* ignore */ }
      }
    }
    try {
      sessionStorage.setItem(key, value);
      sessionSuccess = true;
    } catch (sessionError: any) {
      console.error(`[Storage Error] sessionStorage failed:`, sessionError);
    }
    return localSuccess || sessionSuccess;
  },
  removeItem: (key: string) => {
    let success = false;
    try { localStorage.removeItem(key); success = true; } catch (e: any) { /* ignore */ }
    try { sessionStorage.removeItem(key); success = true; } catch (e: any) { /* ignore */ }
    return success;
  }
};

const BAD_POSTER_CACHE_KEY = 'badPosterUrls';
const MAX_CACHE_SIZE = 500; // Limit cache size to prevent excessive localStorage usage

// Function to get the Set of bad URLs from localStorage
const getBadPosterSet = (): Set<string> => {
  try {
    const cached = safeCacheStorage.getItem(BAD_POSTER_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Ensure it's an array before creating a Set
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch (e) {
    console.error('[BadPosterCache] Error reading cache:', e);
    // If reading fails, clear potentially corrupt cache
    safeCacheStorage.removeItem(BAD_POSTER_CACHE_KEY); 
  }
  return new Set<string>();
};

// Function to save the Set of bad URLs to localStorage
const saveBadPosterSet = (badPosters: Set<string>): void => {
  try {
    // Convert Set to Array for JSON compatibility
    let setToStore = Array.from(badPosters);
    
    // Enforce max cache size (FIFO - remove oldest entries)
    if (setToStore.length > MAX_CACHE_SIZE) {
      // console.warn(`[BadPosterCache] Cache limit (${MAX_CACHE_SIZE}) reached. Removing oldest entries.`);
      setToStore = setToStore.slice(setToStore.length - MAX_CACHE_SIZE);
    }
    
    safeCacheStorage.setItem(BAD_POSTER_CACHE_KEY, JSON.stringify(setToStore));
  } catch (e) {
    console.error('[BadPosterCache] Error saving cache:', e);
  }
};

// Function to add a URL to the bad poster cache
export const addBadPoster = (url: string | undefined | null): void => {
  if (!url || url === 'N/A') return; // Don't cache invalid inputs

  const badPosters = getBadPosterSet();
  if (!badPosters.has(url)) { // Avoid duplicates
    badPosters.add(url);
    saveBadPosterSet(badPosters);
    // console.log(`[BadPosterCache] Added: ${url}`); // Optional logging
  }
};

// Function to check if a URL is in the bad poster cache
export const isBadPoster = (url: string | undefined | null): boolean => {
  if (!url || url === 'N/A') return false; // Treat invalid inputs as not bad

  const badPosters = getBadPosterSet();
  return badPosters.has(url);
};

// Optional: Function to clear the cache (for debugging or user action)
export const clearBadPosterCache = (): void => {
  try {
    safeCacheStorage.removeItem(BAD_POSTER_CACHE_KEY);
    // console.log('[BadPosterCache] Cache cleared.');
  } catch (e) {
    console.error('[BadPosterCache] Error clearing cache:', e);
  }
}; 